import React from "react";
import StatusPill from "./StatusPill";
import { TaskHistoryTooltip, TaskCommentTooltip } from "./TaskRowTooltips";
import { dueLabel, taskSubline } from "./taskHubUtils";

// One-off tasks have no period grid: open ones first (however old), then the
// ones finished in the selected month.

const GRID = "grid-cols-[2.2fr_1.2fr_1.3fr_1.2fr_110px]";

function Row({ row, now, tz, storeId, onOpenTask }) {
  const due = dueLabel(row, now, tz);
  const names = (row.assignees || []).map((a) => a.full_name).filter(Boolean);
  return (
    <div
      onClick={() => onOpenTask(row)}
      className={`grid ${GRID} gap-3 items-center px-4 py-3 border-b border-slate-100 last:border-b-0 text-[13px] cursor-pointer hover:bg-slate-50 ${
        due.overdue ? "bg-rose-50/40" : ""
      }`}
    >
      <span className="min-w-0">
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="font-semibold text-slate-900 truncate">{row.plan_task?.name}</span>
          <span className="flex items-center gap-2 flex-none">
            <TaskHistoryTooltip
              storeId={row.store_id || storeId}
              occurrenceUuid={row.plan_task_assignment_uuid}
              occurrenceDate={row.date}
              tz={tz}
              onClick={() => onOpenTask(row, "timeline")}
            />
            <TaskCommentTooltip
              storeId={row.store_id || storeId}
              occurrenceUuid={row.plan_task_assignment_uuid}
              occurrenceDate={row.date}
              commentCount={row.comment_count || 0}
              onClick={() => onOpenTask(row, "conversation")}
            />
          </span>
        </span>
        <span className="block text-[11px] text-slate-500 truncate">{taskSubline(row.plan_task)}</span>
      </span>
      <span>
        <StatusPill status={row.status} paused={row.paused} />
      </span>
      <span className="min-w-0">
        <span className={`block tabular-nums ${due.overdue ? "font-semibold text-rose-600" : "text-slate-700"}`}>{due.primary}</span>
        {due.secondary ? (
          <span className={`block text-[11px] ${due.overdue ? "text-rose-600" : "text-slate-500"}`}>{due.secondary}</span>
        ) : row.validated_at ? (
          <span className="block text-[11px] text-slate-500">Validated</span>
        ) : row.submitted_at ? (
          <span className="block text-[11px] text-slate-500">Submitted</span>
        ) : null}
      </span>
      <span className="text-slate-700 truncate">{names.length ? names.join(", ") : row.eligible_label || "Anyone"}</span>
      <span className="tabular-nums text-right text-slate-600">
        {row.steps_done || 0}/{row.steps_total || 0}
      </span>
    </div>
  );
}

export default function OverviewOneOffTable({ data, storeId, onOpenTask }) {
  const now = new Date();
  const tz = data?.timezone;
  const open = data?.one_off?.open || [];
  const closed = data?.one_off?.closed || [];
  const upcoming = data?.one_off?.upcoming || [];
  const monthLabel = data?.window?.label || "this month";
  return (
    <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <div className={`grid ${GRID} gap-3 items-center px-4 py-2.5 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-400`}>
        <span>Task</span>
        <span>Status</span>
        <span>Due</span>
        <span>Who</span>
        <span className="text-right">Steps</span>
      </div>
      {open.length === 0 && closed.length === 0 && upcoming.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-slate-500">
          No one-off tasks are open or scheduled, and none were finished in {monthLabel}.
        </div>
      ) : (
        <>
          {open.map((row) => (
            <Row key={row.plan_task_assignment_uuid} row={row} now={now} tz={tz} storeId={storeId} onOpenTask={onOpenTask} />
          ))}
          {closed.length > 0 && (
            <div className="px-4 py-1.5 bg-slate-50 border-y border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Done in {monthLabel}
            </div>
          )}
          {closed.map((row) => (
            <Row key={row.plan_task_assignment_uuid} row={row} now={now} tz={tz} storeId={storeId} onOpenTask={onOpenTask} />
          ))}
          {upcoming.length > 0 && (
            <div className="px-4 py-1.5 bg-slate-50 border-y border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Coming up
            </div>
          )}
          {upcoming.map((row) => (
            <Row key={row.plan_task_assignment_uuid} row={row} now={now} tz={tz} storeId={storeId} onOpenTask={onOpenTask} />
          ))}
        </>
      )}
    </div>
  );
}
